import type { IColor } from '@color';
import { Axis } from '@geom';
import type { ImageSource, ObjectFit } from '@image';
import { determineBaseTextDirection, IString, TextDirection } from '@strings';
import type { LayoutTextOptions, MeasuredText } from '@typography';
import { assertIsAtLeast, assertIsAtMost, immerable } from '@utils';

export const snorfElementIdSymbol = Symbol('snorfElementId');
export type ElementId = string & {
  readonly [snorfElementIdSymbol]: unique symbol;
};

export interface Padding {
  leading: number;
  trailing: number;
  top: number;
  bottom: number;
}

export function axisPadding(axis: Axis, padding: Padding): number {
  switch (axis) {
    case Axis.X:
      return padding.leading + padding.trailing;
    case Axis.Y:
      return padding.top + padding.bottom;
  }
}

export interface CornerRadius {
  topLeft: number;
  topRight: number;
  bottomLeft: number;
  bottomRight: number;
}

export interface SizingMinMax {
  min: number;
  max: number;
}

export enum SizingType {
  FIT = 'fit',
  GROW = 'grow',
  PERCENT = 'percent',
  FIXED = 'fixed'
}

export class SizingAxis {
  [immerable] = true;

  type = SizingType.FIT;
  size: SizingMinMax | number = { min: 0, max: Number.MAX_VALUE };

  fit(size?: Partial<SizingMinMax>): this {
    this.type = SizingType.FIT;
    this.size = { min: 0, max: Number.MAX_VALUE, ...size };
    assertIsAtLeast(this.size.min, 0, 'Sizing min must be >= 0');
    assertIsAtLeast(this.size.max, 0, 'Sizing max must be >= 0');
    assertIsAtMost(this.size.min, this.size.max, 'Sizing min must be <= max');
    return this;
  }

  grow(size?: Partial<SizingMinMax>): this {
    this.type = SizingType.GROW;
    this.size = { min: 0, max: Number.MAX_VALUE, ...size };
    assertIsAtLeast(this.size.min, 0, 'Sizing min must be >= 0');
    assertIsAtLeast(this.size.max, 0, 'Sizing max must be >= 0');
    assertIsAtMost(this.size.min, this.size.max, 'Sizing min must be <= max');
    return this;
  }

  percent(percentOfParent: number): this {
    assertIsAtLeast(percentOfParent, 0, 'Sizing percent must be >= 0');
    assertIsAtMost(percentOfParent, 1, 'Sizing percent must be <= 1');
    this.type = SizingType.PERCENT;
    this.size = percentOfParent;
    return this;
  }

  fixed(size: number): this {
    assertIsAtLeast(size, 0, 'Sizing fixed must be >= 0');
    this.type = SizingType.FIXED;
    this.size = { min: size, max: size };
    return this;
  }
}

export class Sizing {
  [immerable] = true;

  width = new SizingAxis();
  height = new SizingAxis();

  atAxis(axis: Axis): SizingAxis {
    switch (axis) {
      case Axis.X:
        return this.width;
      case Axis.Y:
        return this.height;
    }
  }
}

export enum LayoutDirection {
  LEFT_TO_RIGHT = 'left-to-right',
  RIGHT_TO_LEFT = 'right-to-left',
  TOP_TO_BOTTOM = 'top-to-bottom'
}

export class LayoutConfig {
  [immerable] = true;

  sizing = new Sizing();
  padding: Padding = { leading: 0, top: 0, trailing: 0, bottom: 0 };
  childGap = 0;
  direction = LayoutDirection.LEFT_TO_RIGHT;
  childAlignment = new ChildAlignment();
}

export function textDirectionToLayoutDirection(
  textDirection: TextDirection
): LayoutDirection {
  switch (textDirection) {
    case TextDirection.LTR:
      return LayoutDirection.LEFT_TO_RIGHT;
    case TextDirection.RTL:
      return LayoutDirection.RIGHT_TO_LEFT;
  }
}

export function determineLayoutDirectionFromText(text: string): LayoutDirection {
  const baseTextDirection = determineBaseTextDirection(text);
  return textDirectionToLayoutDirection(baseTextDirection);
}

export enum LayoutAlignmentX {
  LEFT = 'left',
  RIGHT = 'right',
  CENTER = 'center'
}

export enum LayoutAlignmentY {
  TOP = 'top',
  BOTTOM = 'bottom',
  CENTER = 'center'
}

export class ChildAlignment {
  x = LayoutAlignmentX.LEFT;
  y = LayoutAlignmentY.TOP;
}

export interface TextElementData {
  text: IString;
  color: IColor;
  layoutOptions: Partial<LayoutTextOptions>;
  measured?: MeasuredText;
}

export interface ImageElementData {
  source: ImageSource;
  objectFit: ObjectFit;
  opacity: number;
}
